import { UploadFile } from '@/components/upload';

interface BaseMessage {
  id?: number | string;
  /**
   * 1: 客户端请求，2: 服务端响应，3: 服务端请求，4: 客户端响应
   */
  type: 1 | 2 | 3 | 4;
  uid?: string;
  timestamp?: number;
  module?: 'common' | 'im';
  prev?: Message;
  error?: BaseResponse;
  tenant?: 'hll' | '';
  channelId?: number;
  isHistoryMessage?: boolean;
  hasUpdated?: boolean;
  sessionId?: string;
}

export enum MessageEvent {
  /** 用户上线 */
  ONLINE = 'online',

  /** 用户下线 */
  OFFLINE = 'offline',

  CLOSE = 'close',

  /** 用户实时输入内容 */
  INPUT = 'input',

  /** 用户联想输入 */
  THINK = 'think',

  /** 文本消息 */
  MESSAGE = 'message',

  REPLACE = 'messageReplace',

  /** 请求人工 */
  JOIN = 'join',

  /** 转接 */
  TRANSFER = 'transfer',

  /** 评分 */
  RATING = 'rating',

  REGISTER = 'register',

  PING = 'ping',

  ACK = 'ackmessage',

  EVALUATE = 'evaluate',

  FAKE_MESSAGE_EVALUATE = 'fakeMessageEvaluate',

  DISCONNECT = 'disconnect',

  FILE_RISK = 'fileRisk'
}

export enum MessageFlag {
  NO_FLAG = 0,

  /** 正常消息 */
  DEFAULT_MSG = 1000,

  /** 上线欢迎语 */
  ONLINE_MSG = 2001,

  /** 请求接入排队中、排队位置变更 */
  JOIN_QUEUE = 2002,

  /** 消息撤回 */
  UN_SEND_MSG = 3008,

  /** 会话结束,客户离线 */
  SESSION_CLOSED_MSG = 2004,

  /** 当前无在线的客服 */
  NO_ONLINE_CUSTOMER = 2005,

  /** 您当前在黑名单 */
  IN_BALCKLIST = 2006,

  /** 重复请求 */
  REPEATED_REQUEST = 2007,

  /** 消息发送失败 */
  SEND_FAILED_MSG = 2008,

  /** 客服超时未回复 */
  OVERTIME = 2009,

  /** 用户超时未回复 */
  OVERTIME_CUSTOMER = 2010,

  /** 超时会话结束 */
  OVERTIME_CLOSE = 2011,

  /** 客服超时会话结束 */
  OVERTIME_CLOSE_CSUTOMER = 2012,

  MULTI_CHANNEL_LOGOUT = 2015,

  /** 排队超时结束会话 */
  QUEUE_OVERTIME_CLOSE = 2016,

  /** 转人工成功提示 */
  ACCEPT_MSG = 3001,

  /** 客服回复超时提醒(例如：客服妹子忙翻了...) */
  REPLY_TIMEOUT_MSG = 3002,

  /** 客服输入中... */
  INPUTING_MSG = 3003,

  /** 机器人输入中... */
  ROBOT_INPUTING_MSG = 4006,

  CLOSE = 3005,

  SENSITIVE_WORDS = 3100
}

interface BaseResponse {
  ret: number;
  msg: string;
  /** 1-人工会话  2-机器人会话 3-系统会话 */
  serviceType?: 1 | 2 | 3;
  isQueue?: 0 | 1;
  sessionId?: string;
  data?: unknown;
}

export interface MessageEvaluateContent {
  status?: string;
  solved?: number;
  name?: string;
  descContent?: string;
  tag?: string[];
}

/**
 * 用户上线消息
 */
interface OnlineMessage {
  event: MessageEvent.ONLINE;
  data: {
    user_id?: string;
    orderId?: string;
    cityId?: string;
    urlData: {
      groupId?: string;
      openId?: string;
      source?: number;
      channelId?: number;
      identity?: number;
      nickName?: string;
      os?: 'IOS' | 'ANDROID';
      userId?: string;
    };
    reOnline: 0 | 1;
  };
}

/**
 * 坐席获取用户实时输入内容
 */
interface InputMessage {
  event: MessageEvent.INPUT;
  data: {
    content: string;
  };
}

/**
 * 强制下线
 */
interface DisconnectMessage {
  event: MessageEvent.DISCONNECT;
  data: {
    code: number;
    message?: string;
  };
}

/**
 * 用户联想输入
 */
interface ThinkMessage {
  event: MessageEvent.THINK;
  data: {
    question: string;
  };
}

export interface ThinkResponse {
  event: MessageEvent.THINK;
  data: BaseResponse & {
    msgId?: string;
    respInfoList: Array<{
      highlight: string;
      docId: number;
      question: string;
    }>;
  };
}

export interface RegisterMessage {
  event: MessageEvent.REGISTER;
  data: {
    jwt: string;
    version?: string;
    network?: string;
    os?: string;
  };
}

export interface RatingMessageContent {
  /** 评价结果：0：未解决，1：已解决, 2：不评价 */
  solved?: number;
  /** 评价类型：0-邀请评价，1-主动评价 */
  commentType?: number;
  /** 评价内容 */
  remark?: string;
  /** 评价标签 */
  tag?: string;
  /** 人工评价分数（1,2,3,4,5） */
  score?: number;
  /** 评价类型：0：评价机器人，1：评价人工 */
  type?: number;
  /** 是否已评价：0：未评价，1：已评价 */
  commented?: number;
}

export interface RatingMessage {
  event: MessageEvent.RATING;
  data: {
    msgId?: string;
    cmId?: string;
    type: 'rating';
    content: RatingMessageContent;
  };
}

interface NormalMessage<T> {
  event: MessageEvent.MESSAGE;
  data: T & {
    cmId?: string;
    flag?: MessageFlag;
    msgId?: string;
    from?: string;
    fromName?: string;
    to?: string;
    toName?: string;
    isRead?: 0 | 1 | 2;
    createdTime?: number;
    fileRiskStatus?: string;
    evaluationContent?: MessageEvaluateContent;
  };
}

export interface HistoryMessageContent {
  cmId?: string;
  flag?: MessageFlag;
  msgId?: string;
  from?: string;
  fromName?: string;
  to?: string;
  toName?: string;
  isRead?: 0 | 1 | 2;
  createdTime?: number;
  fileRiskStatus?: string;
  evaluationContent?: string;
  sessionId?: string;
}

export type NormalMessageResponse = NormalMessage<{ type: null } & BaseResponse>;

/**
 * 文本消息
 */
export type TextMessage = NormalMessage<{
  type: 'text';
  content: string;
  sensitiveContent?: string;
}>;
export type FlowMessage = NormalMessage<{
  type: 'flow';
  content: string;
  sensitiveContent?: string;
}>;

export type StreamMessage = NormalMessage<{
  type: 'stream';
  content: {
    sessionId?: string;
    /** 流式累计消息 */
    streamAppendMsg?: string;

    /**
     * 流式消息类型
     *
     * 类型名称     状态说明     状态类型     触发条件
     * ----------  ----------  ----------  ----------------------------------------
     * start       流式开始      中间状态    请求大模型的时刻触发
     * first       首段结果      中间状态    拿到流式首段的时候触发
     * middle      中间结果      中间状态    拿到中间结果的时候触发
     * last        最终结果      终态        拿到流式完成标记的时候
     * userBreak   用户打断      终态        用户打断处理完成的时候触发
     * systemBreak 系统打断      终态        首段结果超时、超过最大字数时触发
     */
    streamType?: string;
    correWaterFeedback?: string;
  };
}>;

/**
 * 图片、音频、视频消息
 */
export type FileMessage = NormalMessage<{
  type: 'image' | 'voice' | 'video';
  url?: string;
  file?: UploadFile;
  riskStatus?: string;
  fileRiskStatus?: string;
}>;

interface RecommdItem {
  answer: string;
  question: string;
  docId: string;
}

interface TaskItem {
  id: number;
  index: number;
  qt: string;
}

interface MessageEval {
  evaluateIsOpen?: 0 | 1;
  docId?: string;
  docName?: string;
  correWaterFeedback?: string;
  robotFlag?: number;
}

export type RecommdMessage = NormalMessage<{
  type: 'recommd';
  msgId: string;
  createdTime: string;
  content: {
    answer: string;
    stripe: string;
    sugguestions?: RecommdItem[];
    additional?: string;
    taskList?: TaskItem[];
  } & MessageEval;
}>;

type AnswerMessage = NormalMessage<{
  type: 'answer';
  msgId: string;
  createdTime: string;
  content: {
    answer: string;
    stripe: string;
    sugguestions?: RecommdItem[];
    additional: string;
    taskList?: TaskItem[];
  } & MessageEval;
}>;

type OrderInvitationMessage = NormalMessage<{
  type: 'invitation';
  msgId: string;
  createdTime: string;
  content: {
    title: string;
    content: string;
    orderUrl: string;
    cardInfo?: {
      btnTitle?: string;
      correWaterFeedback?: string;
      link?: string;
      op?: string;
      type?: number;
    };
  } & MessageEval;
}>;

export type OrderCardMessage = NormalMessage<{
  type: 'order';
  content: Array<{
    orderId?: string;
    orderUrl?: string;
    orderType?: number;
    orderTime?: number;
    orderStatus?: string;
    orderStatusCn?: string;
    orderAddress: {
      from?: string;
      to?: string;
    };
    orderRelation?: string;
    extraParams?: {
      order_display_id?: string;
      userName?: string;
      groupid?: string;
      tel?: string;
      op?: string;
      correWaterFeedback?: string;
    };
  }>;
}>;

export type MergeMessageItem =
  | { type: 'text'; data: TextMessage['data'] }
  | { type: 'recommd' | 'answer'; data: RecommdMessage['data']['content'] }
  | { type: 'order'; data: OrderCardMessage['data']['content'] };

export type MergeMessage = NormalMessage<{
  type: 'merge';
  content?: MergeMessageItem[];
}>;

export type RequestRateMessage = NormalMessage<{
  type: 'rating';
  content: RatingMessageContent;
}>;

export interface FileRiskMessage extends BaseMessage {
  event: MessageEvent.FILE_RISK;
  data: {
    type?: string;
    sessionId?: string;
    msgId?: string;
    cmId?: string;
    fileRiskStatus?: string;
    url?: string;
  };
}

export type ConversationMessage =
  | TextMessage
  | FlowMessage
  | StreamMessage
  | FileMessage
  | AnswerMessage
  | RecommdMessage
  | OrderInvitationMessage
  | OrderCardMessage
  | RequestRateMessage;

export interface ReplaceMessage {
  event: MessageEvent.REPLACE;
  data: ConversationMessage['data'];
}

interface OtherMessage {
  event:
    | MessageEvent.OFFLINE
    | MessageEvent.JOIN
    | MessageEvent.TRANSFER
    | MessageEvent.RATING
    | MessageEvent.PING
    | MessageEvent.CLOSE;
  data: null;
}

interface ACKMessage {
  event: MessageEvent.ACK;
  data: {
    msgIds: string[];
  };
}

export interface EvaluateMessage {
  event: MessageEvent.EVALUATE;
  data: {
    /**
     * 3  点赞   4  点踩
     */
    action?: '3' | '4';
    additional?: string;
    docId?: string;
    docName?: string;
    correWaterFeedback?: string;
    robotFlag?: number;
    status?: number;
    msgId?: string;
    evaluationContent?: string;
  };
}

export interface FakeEvaluateMessage {
  event: MessageEvent.FAKE_MESSAGE_EVALUATE;
  data?: {
    content?: MessageEvaluateContent;
    from?: string;
  };
}

export type Message = BaseMessage &
  (
    | OnlineMessage
    | InputMessage
    | ThinkMessage
    | ConversationMessage
    | ReplaceMessage
    | OtherMessage
    | RegisterMessage
    | NormalMessageResponse
    | RatingMessage
    | ACKMessage
    | EvaluateMessage
    | DisconnectMessage
    | MergeMessage
    | FileRiskMessage
    | FakeEvaluateMessage
  );

export type ResponseMessage = BaseMessage & (NormalMessageResponse | ThinkResponse);

interface SensitiveCheckItem {
  black: string;
  type: number;
  /** 命中状态：0命中通过，1:命中嫌疑，2:命中违规 */
  hit: number;
  lid: number;
}

export interface SensitiveCheckResult {
  black?: SensitiveCheckItem[];
  content?: string;
  hit?: number;
}
